import Box from '@mui/material/Box';
import Shimmer from './Shimmer';

// Skeleton for the v5 test detail page: breadcrumb, teal hero, the stack
// of white sections, and the sticky booking card beside them.
//
// Rebuilt against the current layout. The previous version drew the
// pre-v5 page (`phero` / `sidebarL` / `buybox`), so the whole page jumped
// sideways the moment the row arrived.

// Blocks sitting on the teal hero need to read as lighter, not darker.
const onTeal = { background: 'rgba(255,255,255,.22)' };

const Section = ({ children, bg = '#FFFFFF', gap = 1.75 }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap,
      p: 'clamp(22px, 3.4vw, 36px)',
      borderRadius: '28px',
      bgcolor: bg,
    }}
  >
    {children}
  </Box>
);

const TestDetailShimmer = () => (
  <Box aria-label="Loading test">
    {/* breadcrumb */}
    <Box
      sx={{
        width: '100%', maxWidth: 1320, mx: 'auto', pt: 2.5, px: 3,
        display: 'flex', gap: 1, alignItems: 'center',
      }}
    >
      <Shimmer width={44} height={14} rounded={4} />
      <Shimmer width={8} height={14} rounded={4} />
      <Shimmer width={40} height={14} rounded={4} />
      <Shimmer width={8} height={14} rounded={4} />
      <Shimmer width={150} height={14} rounded={4} />
    </Box>

    <Box
      sx={{
        width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 2, pb: 9,
        display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start',
      }}
    >
      {/* ── left column ──────────────────────────────────────────────── */}
      <Box sx={{ flex: '2 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* teal hero */}
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', gap: 2.25,
            p: 'clamp(24px, 4vw, 44px)', borderRadius: '32px',
            bgcolor: 'var(--tl-teal)',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Shimmer width={104} height={25} rounded={999} sx={onTeal} />
            <Shimmer width={176} height={25} rounded={999} sx={onTeal} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Shimmer width="80%" height={44} rounded={10} sx={onTeal} />
            <Shimmer width="46%" height={44} rounded={10} sx={onTeal} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Shimmer width="94%" height={15} rounded={4} sx={onTeal} />
            <Shimmer width="68%" height={15} rounded={4} sx={onTeal} />
          </Box>
          {/* the four fact tiles */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 1 }}>
            {[0, 1, 2, 3].map((i) => <Shimmer key={i} width="100%" height={72} rounded={18} sx={onTeal} />)}
          </Box>
        </Box>

        {/* what this test checks */}
        <Section>
          <Shimmer width="46%" height={28} rounded={8} />
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.75, py: 1, borderTop: '1px solid #EEF3F1' }}>
              <Shimmer width={10} height={18} rounded={4} />
              <Shimmer width={`${68 - i * 9}%`} height={18} rounded={4} />
            </Box>
          ))}
        </Section>

        {/* who should take it */}
        <Section>
          <Shimmer width="38%" height={28} rounded={8} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[132, 96, 158, 112, 142].map((w, i) => (
              <Shimmer key={i} width={w} height={38} rounded={999} />
            ))}
          </Box>
        </Section>

        {/* how to prepare — yellow */}
        <Section bg="var(--tl-yellow)">
          <Shimmer width="42%" height={28} rounded={8} sx={{ background: 'rgba(255,255,255,.65)' }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1 }}>
            {[0, 1, 2].map((i) => (
              <Shimmer key={i} width="100%" height={104} rounded={18} sx={{ background: 'rgba(255,255,255,.65)' }} />
            ))}
          </Box>
        </Section>

        {/* understanding your result */}
        <Section>
          <Shimmer width="54%" height={28} rounded={8} />
          <Shimmer width="100%" height={15} rounded={4} />
          <Shimmer width="74%" height={15} rounded={4} />
          <Shimmer width="100%" height={78} rounded={20} />
        </Section>
      </Box>

      {/* ── sticky booking card ──────────────────────────────────────── */}
      <Box
        component="aside"
        sx={{
          flex: '1 1 320px', minWidth: 0,
          display: 'flex', flexDirection: 'column', gap: 1.5,
          position: { md: 'sticky' }, top: { md: 100 },
        }}
      >
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', gap: 2,
            p: 3, borderRadius: '28px', bgcolor: '#FFFFFF',
            boxShadow: '0 20px 50px -30px rgba(11,43,39,0.4)',
          }}
        >
          <Shimmer width="52%" height={38} rounded={8} />
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
              <Shimmer width={112} height={15} rounded={4} />
              <Shimmer width={78} height={15} rounded={4} />
            </Box>
          ))}
          <Shimmer width="100%" height={54} rounded={16} />
          <Shimmer width="100%" height={54} rounded={16} />
          <Shimmer width="100%" height={88} rounded={16} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75, pt: 1.75, borderTop: '1px solid #EEF3F1' }}>
            {[0, 1, 2].map((i) => <Shimmer key={i} width="100%" height={42} rounded={14} />)}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default TestDetailShimmer;
